// @flow

import type { State } from "@store/reducers";
import type { PaginationType, PaginationExtendedType, SortOrder } from "@types";
import type {
  BusiestClient,
  CallbackQueueDashboardHeadlines,
  CallbackQueueDashboardClientsSummary,
  CallbackQueueDashboardAdvisorsSummary,
  SystemUser,
  MedicalTriageScript,
  MedicalTriageSymptomNote,
  CallScriptManagementType,
  ListUserFilters,
  ClinicalIncident,
  ServiceDeskSettings,
  Allergy,
  Condition,
  Medication,
  Vaccination,
  Provider,
  ServiceType,
  WellbeingCategory,
  ParentAbsenceReason,
  CarePlanActivity,
  CarePlanActivityType,
  CarePlanActivityPeriod,
  CarePlanActivityParent,
  CarePlanActivityCounter,
  SupportPlanActivity,
  SupportPlanActivityCounter,
  SupportPlanJobDutyConfig,
  TriageQuestion,
  TriageQuestionSymptom,
  TriageAdvice,
} from "@modules/management/types";
import { getPagination } from "@selectors";
import type { HealthNotice, ImageLibraryItem } from "@modules/management/types";

// Callback Admin
export * from "./callback-admin";

export const isLoading = (state: State): boolean => state.management.requestsInProgress > 0;

export const selectSystemUsers = (state: State): SystemUser[] => state.management.users.content;

export const selectSystemUsersPagination = (state: State): PaginationExtendedType =>
  getPagination(state.management.users);

export const selectSystemUsersPage = (state: State): number | null =>
  selectSystemUsersPagination(state).pageNumber;

export const selectSystemUsersSort = (state: State): Array<SortOrder> | null =>
  (((((state || {}).management || {}).users || {}).pagination || {}).sort || {}).orders;

export const selectSystemUsersSearch = (state: State): ListUserFilters =>
  (((state || {}).form || {}).ListUsersFilter || {}).values || {};

export const selectAllergies = (state: State): Allergy[] => state.management.allergies.content;

export const selectAllergiesPagination = (state: State): PaginationExtendedType =>
  getPagination(state.management.allergies);

export const selectAllergiesPage = (state: State): number | null =>
  selectAllergiesPagination(state).pageNumber;

export const selectAllergyModal = (state: State): { visible: boolean, data: Allergy | null } =>
  state.management.modals.allergy;

export const selectConditions = (state: State): Condition[] => state.management.conditions.content;

export const selectConditionsPagination = (state: State): PaginationExtendedType =>
  getPagination(state.management.conditions);

export const selectConditionsPage = (state: State): number | null =>
  selectConditionsPagination(state).pageNumber;

export const selectConditionModal = (state: State): { visible: boolean, data: Condition | null } =>
  state.management.modals.condition;

export const selectMedications = (state: State): Medication[] =>
  state.management.medications.content;

export const selectMedicationsPagination = (state: State): PaginationExtendedType =>
  getPagination(state.management.medications);

export const selectMedicationsPage = (state: State): number | null =>
  selectMedicationsPagination(state).pageNumber;

export const selectMedicationTypes = (state: State): string[] => state.management.medicationTypes;

export const selectMedicationModal = (
  state: State,
): { visible: boolean, data: Medication | null } => state.management.modals.medication;

export const selectVaccinations = (state: State): Vaccination[] =>
  state.management.vaccinations.content;

export const selectVaccinationsPagination = (state: State): PaginationExtendedType =>
  getPagination(state.management.vaccinations);

export const selectVaccinationsPage = (state: State): number | null =>
  selectVaccinationsPagination(state).pageNumber;

export const selectVaccinationModal = (
  state: State,
): { visible: boolean, data: Vaccination | null } => state.management.modals.vaccination;

export const selectProviders = (state: State): Provider[] => state.management.providers.content;

export const selectProvidersPagination = (state: State): PaginationExtendedType =>
  getPagination(state.management.providers);

export const selectProvidersPage = (state: State): number | null =>
  selectProvidersPagination(state).pageNumber;

export const selectProviderModal = (
  state: State,
): { visible: boolean, data: Provider | null, logoUrl: string | null } =>
  state.management.modals.provider;

export const selectServiceTypes = (state: State): ServiceType[] =>
  state.management.serviceTypes.content;

export const selectServiceTypesPagination = (state: State): PaginationExtendedType =>
  getPagination(state.management.serviceTypes);

export const selectServiceTypesPage = (state: State): number | null =>
  selectServiceTypesPagination(state).pageNumber;

export const selectServiceTypeModal = (
  state: State,
): { visible: boolean, data: ServiceType | null } => state.management.modals.serviceType;

export const selectWellbeingCategories = (state: State): WellbeingCategory[] =>
  state.management.wellbeingCategories;

export const selectParentAbsenceReasons = (state: State): ParentAbsenceReason[] =>
  state.management.parentAbsenceReasons;

export const selectCarePlanActivities = (state: State): CarePlanActivity[] =>
  state.management.carePlanActivities.content;

export const selectCarePlanActivitiesPagination = (state: State): PaginationExtendedType =>
  getPagination(state.management.carePlanActivities);

export const selectCarePlanActivitiesPage = (state: State): number | null =>
  selectCarePlanActivitiesPagination(state).pageNumber;

export const selectCarePlanActivityTypes = (state: State): CarePlanActivityType[] =>
  state.management.carePlanActivityTypes;

export const selectCarePlanActivityPeriods = (state: State): CarePlanActivityPeriod[] =>
  state.management.carePlanActivityPeriods;

export const selectCarePlanActivityParents = (state: State): CarePlanActivityParent[] =>
  state.management.carePlanActivityParents;

export const selectCarePlanActivityCounters = (state: State): CarePlanActivityCounter[] =>
  state.management.carePlanActivityCounters;

export const selectSupportPlanActivities = (state: State): SupportPlanActivity[] =>
  state.management.supportPlanActivities.content;

export const selectSupportPlanActivitiesPagination = (state: State): PaginationExtendedType =>
  getPagination(state.management.supportPlanActivities);

export const selectSupportPlanActivitiesPage = (state: State): number | null =>
  selectSupportPlanActivitiesPagination(state).pageNumber;

export const selectSupportPlanActivityCounters = (state: State): SupportPlanActivityCounter[] =>
  state.management.supportPlanActivityCounters;

export const selectSupportPlanJobDutyConfigs = (state: State): SupportPlanJobDutyConfig[] =>
  state.management.supportPlanJobDutyConfigs;

export const selectSupportPlanActivityModal = (
  state: State,
): { visible: boolean, data: SupportPlanActivity | null } =>
  state.management.modals.supportPlanActivity;

export const selectCarePlanActivityModal = (
  state: State,
): { visible: boolean, data: CarePlanActivity | null } => state.management.modals.carePlanActivity;

export const selectTriageQuestions = (state: State): TriageQuestion[] =>
  state.management.triageQuestions.content;

export const selectTriageQuestionsPagination = (state: State): PaginationExtendedType =>
  getPagination(state.management.triageQuestions);

export const selectTriageQuestionsPage = (state: State): number | null =>
  selectTriageQuestionsPagination(state).pageNumber;

export const selectTriageQuestionSymptoms = (state: State): TriageQuestionSymptom[] =>
  state.management.triageQuestionSymptoms;

export const selectTriageQuestionModal = (
  state: State,
): { visible: boolean, data: TriageQuestion | null } => state.management.modals.triageQuestion;

export const selectTriageAdvices = (state: State): TriageAdvice[] =>
  state.management.triageAdvices.content;

export const selectTriageAdvicesPagination = (state: State): PaginationExtendedType =>
  getPagination(state.management.triageAdvices);

export const selectTriageAdvicesPage = (state: State): number | null =>
  selectTriageAdvicesPagination(state).pageNumber;

export const selectTriageAdviceSymptoms = (state: State): TriageQuestionSymptom[] =>
  state.management.triageAdviceSymptoms;

export const selectTriageAdviceModal = (
  state: State,
): { visible: boolean, data: TriageAdvice | null } => state.management.modals.triageAdvice;

export const selectEmployees = (state: State): Array<Object> => state.management.employees.content;

export const selectEmployeesPagination = (state: State): PaginationExtendedType =>
  getPagination(state.management.employees);

export const selectEmployeesPage = (state: State): number | null =>
  selectEmployeesPagination(state).pageNumber;

export const selectEmployeesSort = (state: State): Array<SortOrder> | null =>
  (((((state || {}).management || {}).employees || {}).pagination || {}).sort || {}).orders;

export const isAddUserModalOpen = (state: State): boolean => state.management.modals.add.visible;

export const isEditUserModalOpen = (state: State): boolean => state.management.modals.edit.visible;

export const selectAddedUser = (state: State): SystemUser | null =>
  state.management.modals.add.user;

export const selectEditedUser = (state: State): SystemUser | null =>
  state.management.modals.edit.user;

export const selectBusiestClients = (state: State): Array<BusiestClient> =>
  state.management.busiestClients.content;

export const selectBusiestClientsPagination = (state: State): PaginationExtendedType =>
  getPagination(state.management.busiestClients);

export const selectBusiestClientsPage = (state: State): number | null =>
  selectBusiestClientsPagination(state).pageNumber;

export const selectBusiestClientsSort = (state: State): Array<SortOrder> | null =>
  (((((state || {}).management || {}).busiestClients || {}).pagination || {}).sort || {}).orders;

export const selectCallbackQueueDashboardHeadlines = (
  state: State,
): CallbackQueueDashboardHeadlines => {
  return state.management.dashboard.headlines;
};
export const selectCallbackQueueDashboardClientsSummary = (
  state: State,
): CallbackQueueDashboardClientsSummary[] => {
  return state.management.dashboard.clientsSummary;
};
export const selectCallbackQueueDashboardAdvisorsSummary = (
  state: State,
): CallbackQueueDashboardAdvisorsSummary[] => {
  return state.management.dashboard.advisorsSummary;
};

export const getDashboardFromToDaterange = (state: State) => {
  return (state.form["from-to-refresh-form"] || {}).values;
};

export const getMedicalTriageScripts = (state: State): MedicalTriageScript[] =>
  state.management.medicalTriageScripts.content;

export const getMedicalTriageSymptoms = (state: State): MedicalTriageSymptomNote[] =>
  state.management.medicalTriageSymptoms.content;

export const getCallScripts = (state: State): CallScriptManagementType[] =>
  state.management.callScripts.content;

export const getIsEditCallflowScriptModalOpen = (state: State): boolean =>
  state.management.modals.editCallScript.visible;

export const getCallScriptFormData = (state: State): Object =>
  state.management.modals.editCallScript.formData;

export const getCallScriptId = (state: State): number | null =>
  state.management.modals.editCallScript.id;

export const getCallScriptName = (state: State): string | null =>
  state.management.modals.editCallScript.displayName;

export const getIsMedicalTriageScriptModalOpen = (state: State): boolean =>
  state.management.modals.editMedicalTriageScript.visible;

export const getMedicalTriageScriptName = (state: State): string | null =>
  state.management.modals.editMedicalTriageScript.displayName;

export const getMedicalTriageScriptId = (state: State): number | null =>
  state.management.modals.editMedicalTriageScript.id;

export const getMedicalTriageScriptFormData = (state: State): Object =>
  state.management.modals.editMedicalTriageScript.formData;

export const getIsMedicalTriageSymptomModalOpen = (state: State): boolean =>
  state.management.modals.editMedicalTriageSymptom.visible;

export const getMedicalTriageSymptomId = (state: State): number | null =>
  state.management.modals.editMedicalTriageSymptom.id;

export const getMedicalTriageSymptomFormData = (state: State): Object =>
  state.management.modals.editMedicalTriageSymptom.formData;

export const getMedicalTriageSymptomName = (state: State): string | null =>
  state.management.modals.editMedicalTriageSymptom.name;

export const getSystemMessagesLoginText = (state: State): string | null =>
  (state.management.login || {}).message || "";

export const getSystemMessagesReleaseNote = (state: State): Object | null =>
  state.management.releaseNote;

export const openLoginMessageModal = (state: State): boolean =>
  state.management.modals.editLogin.visible;

export const openReleaseNoteModal = (state: State): boolean =>
  state.management.modals.releaseNote.visible;

export const isLogNewIncidentModalVisible = (state: State): boolean =>
  state.management.modals.logNewIncident.visible;
export const getViewEditIncidentModal = (
  state: State,
): { data: ClinicalIncident | null, visible: boolean } => state.management.modals.viewEditIncident;

export const getIncidentsRegister = (state: State): Object => state.management.incidentRegister;
export const getIncidentsTypes = (state: State): Array<{ id: number, name: string }> =>
  state.management.incidentTypes;
export const getIncidentsCauses = (state: State): Array<{ id: number, name: string }> =>
  state.management.incidentCauses;
export const getCallHandlers = (state: State): Array<{ id: number, name: string }> =>
  state.management.callHandlers;
export const getIncidentRatesReport = (
  state: State,
): {
  MONTH: {
    content: Array<Object>,
    pagination: PaginationType,
  },
  CAUSE: {
    content: Array<Object>,
    pagination: PaginationType,
  },
  headlines: {
    total: string,
    totalNi: string,
    clinicalIncidentPercent: string,
    mostCommonCause: string,
  },
} => state.management.incidentRatesReport;

export const getServiceDeskManagement = (state: State): ServiceDeskSettings =>
  state.management.serviceDesk;

export const healthNoticesList = (state: State): HealthNotice[] =>
  state.management.healthNotices.list.content;

export const selectCurrentNotice = (state: State): HealthNotice | null =>
  state.management.healthNotices.current;

export const imagesList = (state: State): ImageLibraryItem[] =>
  state.management.images.list.content;

export const imagesPagination = (state: State): PaginationExtendedType =>
  getPagination(state.management.images.list);
